import { Link, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { useQueryClient } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";

import { Loading } from "src/common/Loading";
import { CellKit, CellKitAssignment } from "src/common/types";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderTabs,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { TabLink } from "src/layout/TabLink";
import { getCellKitQueryKey } from "src/routes/ProductUnits/CellKits/queries";

const CellKitAssignmentLogsTable = ({
  assignments,
}: {
  assignments: CellKitAssignment[];
}) => {
  if (!assignments.length) {
    return <Text>This cell kit has not been assigned to an intercom.</Text>;
  }
  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>Date</Th>
          <Th>Name</Th>
          <Th>Status</Th>
        </Tr>
      </Thead>
      <Tbody>
        {assignments.map((assignment) => {
          return (
            <React.Fragment key={assignment.cellKitAssignmentId}>
              {assignment.activatedAt && (
                <Tr>
                  <Td>{format(new Date(assignment.activatedAt), "P")}</Td>
                  <Td>{`${assignment.activatedByGatehawkUser?.user.firstName} ${assignment.activatedByGatehawkUser?.user.lastName}`}</Td>
                  <Td>Activated</Td>
                </Tr>
              )}
              <Tr>
                <Td>{format(new Date(assignment.createdAt), "P")}</Td>
                <Td>{`${assignment.assignedByDealerUser?.user.firstName} ${assignment.assignedByDealerUser?.user.lastName}`}</Td>
                <Td>Pending Activation</Td>
              </Tr>
            </React.Fragment>
          );
        })}
      </Tbody>
    </Table>
  );
};

export const CellKitAssignmentLogs = () => {
  const { id } = useParams() as { id: string };
  const queryClient = useQueryClient();
  const cellKitQueryKey = getCellKitQueryKey(id);

  const data = queryClient.getQueryData<CellKit>(cellKitQueryKey);

  const orderedAssignments = useMemo(() => {
    if (!data || !data.assignments.length) return [];

    return data.assignments.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [data]);

  if (!data) return <Loading />;

  return (
    <>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={"/product-units/cell-kits"}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>{`${data.serialNumber} - Assignment Logs`}</PageHeaderTitle>
        <PageHeaderTabs>
          <TabLink
            key="details"
            to={`/product-units/cell-kits/${id}/details`}
            label="Cell Kit Details"
            end
          />
          <TabLink
            key="financials"
            to={`/product-units/cell-kits/${id}/assignment-logs`}
            label="Assignment Logs"
            end
          />
        </PageHeaderTabs>
      </PageHeader>
      <PageContent>
        <CellKitAssignmentLogsTable assignments={orderedAssignments} />
      </PageContent>
    </>
  );
};
